/**
 * Legal Documents Index
 * 
 * Exports current versions of all legal documents with automatic language detection.
 * Supports version history for viewing previous versions.
 */
import i18n from '../../i18n';

// v1 imports
import { TERMS_V1_KO } from './terms/v1_ko';
import { TERMS_V1_EN } from './terms/v1_en';
import { PRIVACY_V1_KO } from './privacy/v1_ko';
import { PRIVACY_V1_EN } from './privacy/v1_en';
import { LOCATION_V1_KO } from './location/v1_ko';
import { LOCATION_V1_EN } from './location/v1_en';

// v2 imports
import { TERMS_V2_KO } from './terms/v2_ko';
import { TERMS_V2_EN } from './terms/v2_en';
import { PRIVACY_V2_KO } from './privacy/v2_ko';
import { PRIVACY_V2_EN } from './privacy/v2_en';
import { LOCATION_V2_KO } from './location/v2_ko';
import { LOCATION_V2_EN } from './location/v2_en';

/**
 * Detect if user is Korean based on i18n language or browser settings
 */
const isKorean = () => {
    const lang = i18n.language || navigator.language || 'en';
    return lang.startsWith('ko');
};

/**
 * Current legal document versions (for storage and agreement check)
 */
export const LEGAL_VERSIONS = {
    terms: 'v2',
    privacy: 'v2',
    location: 'v2'
};

/**
 * All available versions (for version history feature)
 */
export const ALL_VERSIONS = {
    terms: ['v1', 'v2'],
    privacy: ['v1', 'v2'],
    location: ['v1', 'v2']
};

/**
 * Version data map for all documents
 */
const LEGAL_DATA = {
    terms: {
        v1: { ko: TERMS_V1_KO, en: TERMS_V1_EN },
        v2: { ko: TERMS_V2_KO, en: TERMS_V2_EN }
    },
    privacy: {
        v1: { ko: PRIVACY_V1_KO, en: PRIVACY_V1_EN },
        v2: { ko: PRIVACY_V2_KO, en: PRIVACY_V2_EN }
    },
    location: {
        v1: { ko: LOCATION_V1_KO, en: LOCATION_V1_EN },
        v2: { ko: LOCATION_V2_KO, en: LOCATION_V2_EN }
    }
};

/**
 * Get specific version of a document
 * @param {string} type - 'terms' | 'privacy' | 'location'
 * @param {string} version - 'v1' | 'v2' | etc.
 * @returns {object} Document object with version, effectiveDate, language, title, content
 */
export const getDocumentByVersion = (type, version) => {
    const langKey = isKorean() ? 'ko' : 'en';
    return LEGAL_DATA[type]?.[version]?.[langKey] || null;
};

/**
 * Get current Terms of Service based on user's language
 */
export const getCurrentTerms = () => getDocumentByVersion('terms', LEGAL_VERSIONS.terms);

/**
 * Get current Privacy Policy based on user's language
 */
export const getCurrentPrivacy = () => getDocumentByVersion('privacy', LEGAL_VERSIONS.privacy);

/**
 * Get current Location Consent based on user's language
 */
export const getCurrentLocation = () => getDocumentByVersion('location', LEGAL_VERSIONS.location);

/**
 * Get all current legal documents
 */
export const getAllCurrentLegal = () => ({
    terms: getCurrentTerms(),
    privacy: getCurrentPrivacy(),
    location: getCurrentLocation()
});
